import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useInvoices } from '@hooks/useSalesAndBilling'

const statusColors: Record<string, string> = {
  PENDING: 'badge-warning',
  PARTIAL: 'badge-info',
  PAID: 'badge-success',
  OVERDUE: 'badge-danger',
}

export function InvoiceListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = useInvoices(search, status)

  const invoices = data?.data?.items || []
  const total = data?.data?.total || 0
  const pageSize = data?.data?.pageSize || 20
  const totalPages = data?.data?.totalPages || 0

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-600 text-sm mt-1">
            Manage invoices and track payments
          </p>
        </div>
      </div>

      <div className="card">
        <div className="space-y-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              type="text"
              placeholder="Search by invoice or store..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="w-full"
            />

            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value)
                setPage(1)
              }}
              className="w-full"
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="PARTIAL">Partial</option>
              <option value="PAID">Paid</option>
              <option value="OVERDUE">Overdue</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            Failed to load invoices. Please try again.
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {search ? 'No invoices found.' : 'No invoices yet.'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-2 font-semibold">Invoice No.</th>
                    <th className="text-left px-4 py-2 font-semibold">Store</th>
                    <th className="text-left px-4 py-2 font-semibold">SO No.</th>
                    <th className="text-left px-4 py-2 font-semibold">Date</th>
                    <th className="text-right px-4 py-2 font-semibold">Total Amount</th>
                    <th className="text-left px-4 py-2 font-semibold">Status</th>
                    <th className="text-left px-4 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="border-b hover:bg-gray-50 transition"
                    >
                      <td className="px-4 py-3 font-mono font-medium">
                        {invoice.invoiceNo}
                      </td>
                      <td className="px-4 py-3">
                        {invoice.salesOrder.store.storeName}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {invoice.salesOrder.soNumber}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {new Date(invoice.invoiceDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        ₹{invoice.totalAmount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${statusColors[invoice.paymentStatus]}`}>
                          {invoice.paymentStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() =>
                            navigate(
                              `/dashboard/sales/invoices/${invoice.id}`
                            )
                          }
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Showing {(page - 1) * pageSize + 1} to{' '}
                  {Math.min(page * pageSize, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-2 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
